/**
 * 동시 실행 상한을 둔 병렬 map (rules.md §7).
 *
 * 서로 독립인 요청은 병렬로 보낸다. 하지만 한 번에 모두 보내지는 않는다. 상한이 없으면
 * HTTP/1.1 에서는 브라우저의 origin 연결 한도(6)에서 큐로 기다리기만 한다. HTTP/2 에서는 모든
 * 요청이 같은 순간 서버에 도착해, 함께 열린 다른 화면의 요청을 늦춘다.
 *
 * 결과는 **입력 인덱스 순서**로 돌려준다. 호출부가 인덱스로 원본 항목을 찾기 때문에
 * 응답 도착 순서가 결과 순서를 바꾸면 안 된다.
 *
 * worker 가 예외를 던지면 전체가 reject 된다. 항목별 실패를 허용해야 하는 호출부는 worker 안에서
 * 직접 예외를 잡고 대체값을 돌려준다. 이 헬퍼는 실패 정책을 정하지 않는다.
 *
 * Args:
 *   items: 처리할 항목들.
 *   worker: 항목 하나를 처리한다. 원본 인덱스를 함께 받는다.
 *   concurrency: 동시에 진행할 worker 수. 1 미만이면 1로 바꾼다.
 *
 * Returns:
 *   입력과 같은 순서의 결과 배열.
 */
export async function mapWithConcurrency<Item, Result>(
  items: readonly Item[],
  worker: (item: Item, index: number) => Promise<Result>,
  concurrency: number,
): Promise<Result[]> {
  const results = new Array<Result>(items.length);
  let nextIndex = 0;
  const workerCount = Math.max(1, Math.min(concurrency, items.length));
  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      for (let index = nextIndex++; index < items.length; index = nextIndex++) {
        results[index] = await worker(items[index], index);
      }
    }),
  );
  return results;
}
